import type { TrustpilotPromptMilestone } from "@/shared/constants/trustpilot.const";
import { TRUSTPILOT_PROMPT_MILESTONES } from "@/shared/constants/trustpilot.const";
import trpc from "@/shared/utils/trpc";
import {
  REVIEW_PLATFORM_SOURCES,
  type ReviewPlatformSource,
} from "@shared/trustpilot";

function storageKey(userId: string, milestone: TrustpilotPromptMilestone): string {
  return `gt.trustpilotPrompt.v1.${userId}.${milestone}`;
}

function pendingStorageKey(userId: string): string {
  return `gt.trustpilotPromptPending.v1.${userId}`;
}

function platformStorageKey(userId: string, platform: ReviewPlatformSource): string {
  return `gt.reviewPlatformClicked.v1.${userId}.${platform}`;
}

export const TRUSTPILOT_PROMPT_QUEUED_EVENT = "gt:trustpilot-prompt-queued";

export function shouldShowTrustpilotPrompt(
  userId: string | null | undefined,
  milestone: TrustpilotPromptMilestone,
): boolean {
  if (!userId) return false;
  try {
    return localStorage.getItem(storageKey(userId, milestone)) !== "1";
  } catch {
    return true;
  }
}

export function markTrustpilotPromptShown(
  userId: string | null | undefined,
  milestone: TrustpilotPromptMilestone,
): void {
  if (!userId) return;
  try {
    localStorage.setItem(storageKey(userId, milestone), "1");
  } catch {
    /* ignore quota / private mode */
  }
}

export function hasClickedReviewPlatform(
  userId: string | null | undefined,
  platform: ReviewPlatformSource,
): boolean {
  if (!userId) return false;
  try {
    return localStorage.getItem(platformStorageKey(userId, platform)) === "1";
  } catch {
    return false;
  }
}

export function markReviewPlatformClicked(
  userId: string | null | undefined,
  platform: ReviewPlatformSource,
): void {
  if (!userId) return;
  try {
    localStorage.setItem(platformStorageKey(userId, platform), "1");
  } catch {
    /* ignore quota / private mode */
  }
}

export function getRemainingReviewPlatforms(
  userId: string | null | undefined,
): ReviewPlatformSource[] {
  return REVIEW_PLATFORM_SOURCES.filter((platform) => !hasClickedReviewPlatform(userId, platform));
}

export function shouldQueueTrustpilotPrompt(
  userId: string | null | undefined,
  milestone: TrustpilotPromptMilestone,
): boolean {
  return shouldShowTrustpilotPrompt(userId, milestone) && getRemainingReviewPlatforms(userId).length > 0;
}

/** Persist milestone dismissal/completion to DB and sync localStorage. */
export async function persistTrustpilotPromptCompleted(
  userId: string | null | undefined,
  milestone: TrustpilotPromptMilestone,
): Promise<void> {
  if (!userId) return;
  markTrustpilotPromptShown(userId, milestone);
  const pending = readPendingTrustpilotPrompt(userId);
  if (pending === milestone) {
    clearPendingTrustpilotPrompt(userId);
  }
  try {
    await trpc.trustpilot.markCompleted.mutate({ milestone });
  } catch (err) {
    console.error("[trustpilot] markCompleted failed:", err);
  }
}

/** Persist only the chosen review platform; do not dismiss the whole milestone. */
export async function persistReviewPlatformClick(
  userId: string | null | undefined,
  milestone: TrustpilotPromptMilestone,
  platform: ReviewPlatformSource,
): Promise<void> {
  if (!userId) return;
  markReviewPlatformClicked(userId, platform);
  const pending = readPendingTrustpilotPrompt(userId);
  if (pending === milestone) {
    clearPendingTrustpilotPrompt(userId);
  }
  try {
    await trpc.trustpilot.markCompleted.mutate({
      milestone,
      platform,
    });
  } catch (err) {
    console.error("[trustpilot] platform click persist failed:", err);
  }
}

/** Pull DB completions into localStorage; push any local-only marks up to DB. */
export async function syncTrustpilotPromptsFromServer(
  userId: string | null | undefined,
): Promise<TrustpilotPromptMilestone[]> {
  if (!userId) return [];
  try {
    const res = await trpc.trustpilot.getCompleted.query();
    const completed = (res.completed ?? []) as TrustpilotPromptMilestone[];
    const clickedPlatforms = (res.clickedPlatforms ?? []) as ReviewPlatformSource[];
    for (const milestone of completed) {
      markTrustpilotPromptShown(userId, milestone);
    }
    for (const platform of clickedPlatforms) {
      markReviewPlatformClicked(userId, platform);
    }

    // Migrate existing local-only marks to DB (one-time per milestone).
    for (const milestone of TRUSTPILOT_PROMPT_MILESTONES) {
      if (!shouldShowTrustpilotPrompt(userId, milestone) && !completed.includes(milestone)) {
        void persistTrustpilotPromptCompleted(userId, milestone);
      }
    }

    return completed;
  } catch (err) {
    console.error("[trustpilot] getCompleted failed:", err);
    return [];
  }
}

/** Queue a prompt so AppLayout can show it after navigation/remount. */
export function queueTrustpilotPrompt(
  userId: string | null | undefined,
  milestone: TrustpilotPromptMilestone,
): void {
  if (!userId || !shouldQueueTrustpilotPrompt(userId, milestone)) return;
  try {
    sessionStorage.setItem(pendingStorageKey(userId), milestone);
  } catch {
    /* ignore */
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent(TRUSTPILOT_PROMPT_QUEUED_EVENT, {
        detail: { userId, milestone },
      }),
    );
  }
}

export function readPendingTrustpilotPrompt(
  userId: string | null | undefined,
): TrustpilotPromptMilestone | null {
  if (!userId) return null;
  try {
    const raw = sessionStorage.getItem(pendingStorageKey(userId));
    if (!raw) return null;
    if (!(TRUSTPILOT_PROMPT_MILESTONES as readonly string[]).includes(raw)) return null;
    return raw as TrustpilotPromptMilestone;
  } catch {
    return null;
  }
}

export function clearPendingTrustpilotPrompt(userId: string | null | undefined): void {
  if (!userId) return;
  try {
    sessionStorage.removeItem(pendingStorageKey(userId));
  } catch {
    /* ignore */
  }
}
